import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { posAPI, authAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { FaArrowLeft, FaSignOutAlt, FaStore, FaRupeeSign, FaCheckCircle, FaTimesCircle, FaSpinner } from 'react-icons/fa';

const POLL_INTERVAL = 4000;
const MAX_POLLS = 30;

export default function POSCollect() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [phase, setPhase] = useState('input'); // input | polling | success | failed
  const [txn, setTxn] = useState(null);
  const pollRef = useRef(null);
  const pollCount = useRef(0);

  useEffect(() => () => clearInterval(pollRef.current), []);

  const handleLogout = async () => {
    clearInterval(pollRef.current);
    try { await authAPI.logout(); } catch (_) {}
    logout();
    navigate('/login', { replace: true });
  };

  const startPolling = (txnId) => {
    pollCount.current = 0;
    pollRef.current = setInterval(async () => {
      pollCount.current++;
      try {
        const r = await posAPI.checkStatus(txnId);
        const t = r.data?.data || r.data;
        if (t?.status === 'success') {
          clearInterval(pollRef.current);
          setTxn(t);
          setPhase('success');
        } else if (t?.status === 'failed') {
          clearInterval(pollRef.current);
          setTxn(t);
          setPhase('failed');
        }
      } catch (_) {}

      if (pollCount.current >= MAX_POLLS) {
        clearInterval(pollRef.current);
        setPhase('failed');
        setError('Payment timed out. Please check transaction history.');
      }
    }, POLL_INTERVAL);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (!amt || amt < 1) { setError('Enter a valid amount (min ₹1)'); return; }

    setLoading(true);
    setError('');
    try {
      const r = await posAPI.initPayment({ amount: amt, note, solution_type: 'EDC' });
      const t = r.data?.data || r.data;
      setTxn(t);
      setPhase('polling');
      startPolling(t.a1x_transaction_id || t.transaction_id);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to initiate payment. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const [cancelling, setCancelling] = useState(false);

  const handleNewPayment = () => {
    clearInterval(pollRef.current);
    setPhase('input');
    setAmount('');
    setNote('');
    setError('');
    setTxn(null);
  };

  const handleCancel = async () => {
    clearInterval(pollRef.current);
    const txnId = txn?.a1x_transaction_id || txn?.transaction_id;
    if (txnId) {
      setCancelling(true);
      try { await posAPI.cancelPayment(txnId); } catch (_) {}
      setCancelling(false);
    }
    handleNewPayment();
  };

  return (
    <div style={S.page}>
      <nav style={S.nav}>
        <div style={S.navBrand}><FaStore style={{ marginRight: 8 }} />A1xPay POS</div>
        <div style={S.navRight}>
          <button onClick={() => navigate('/pos/dashboard')} style={S.navBtn}><FaArrowLeft /></button>
          <button onClick={handleLogout} style={S.navBtn}><FaSignOutAlt /></button>
        </div>
      </nav>

      <div style={S.body}>
        {phase === 'input' && (
          <div style={S.card}>
            <h1 style={S.title}>Collect Payment</h1>
            <p style={S.sub}>Enter the amount to charge the customer.</p>

            {error && <div style={S.errBox}>{error}</div>}

            <form onSubmit={handleSubmit}>
              <div style={S.amtRow}>
                <span style={S.rupee}><FaRupeeSign /></span>
                <input
                  type="number" value={amount} min="1" step="0.01"
                  onChange={e => { setAmount(e.target.value); setError(''); }}
                  placeholder="0.00" style={S.amtInput}
                  autoFocus disabled={loading}
                />
              </div>

              <div style={{ marginBottom: 24 }}>
                <label style={S.fieldLabel}>Note / Description (optional)</label>
                <input
                  type="text" value={note} onChange={e => setNote(e.target.value)}
                  placeholder="e.g. Bill payment, Order #123"
                  style={S.noteInput} disabled={loading}
                />
              </div>

              <button type="submit" disabled={loading || !amount} style={{ ...S.btn, opacity: (!amount || loading) ? 0.6 : 1 }}>
                {loading ? 'Initiating…' : `Charge ₹${parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`}
              </button>
            </form>
          </div>
        )}

        {phase === 'polling' && (
          <div style={{ ...S.card, textAlign: 'center' }}>
            <div style={S.bigIcon}><FaSpinner style={{ animation: 'spin 1s linear infinite', color: '#7c3aed' }} /></div>
            <h2 style={S.statusTitle}>Waiting for payment…</h2>
            <p style={S.statusSub}>Ask the customer to tap / swipe / insert card on the POS machine.</p>
            <div style={S.txnBox}>
              <div style={S.txnRow}><span>Amount</span><span>₹{txn?.amount?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span></div>
              <div style={S.txnRow}><span>Transaction ID</span><span style={{ fontFamily: 'monospace', fontSize: 12 }}>{txn?.a1x_transaction_id}</span></div>
            </div>
            <button onClick={handleCancel} disabled={cancelling} style={{ ...S.btn, background: '#F1F5F9', color: '#4a4a6a', marginTop: 0 }}>
              {cancelling ? 'Cancelling…' : 'Cancel Payment'}
            </button>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        {phase === 'success' && (
          <div style={{ ...S.card, textAlign: 'center' }}>
            <div style={S.bigIcon}><FaCheckCircle style={{ color: '#16A34A' }} /></div>
            <h2 style={{ ...S.statusTitle, color: '#16A34A' }}>Payment Successful!</h2>
            <p style={S.statusSub}>Transaction completed successfully.</p>
            <div style={S.txnBox}>
              <div style={S.txnRow}><span>Amount</span><span style={{ fontWeight: 700 }}>₹{txn?.amount?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span></div>
              <div style={S.txnRow}><span>Transaction ID</span><span style={{ fontFamily: 'monospace', fontSize: 12 }}>{txn?.a1x_transaction_id}</span></div>
              {txn?.phonepe_transaction_id && <div style={S.txnRow}><span>PhonePe Ref</span><span style={{ fontFamily: 'monospace', fontSize: 12 }}>{txn.phonepe_transaction_id}</span></div>}
              {txn?.payment_type && <div style={S.txnRow}><span>Payment Type</span><span>{txn.payment_type}</span></div>}
            </div>
            <button onClick={handleNewPayment} style={S.btn}>New Payment</button>
            <button onClick={() => navigate('/pos/transactions')} style={{ ...S.btn, background: '#F1F5F9', color: '#14142b', marginTop: 8 }}>View History</button>
          </div>
        )}

        {phase === 'failed' && (
          <div style={{ ...S.card, textAlign: 'center' }}>
            <div style={S.bigIcon}><FaTimesCircle style={{ color: '#DC2626' }} /></div>
            <h2 style={{ ...S.statusTitle, color: '#DC2626' }}>Payment Failed</h2>
            <p style={S.statusSub}>{error || 'The transaction could not be completed. Please try again.'}</p>
            {txn && (
              <div style={S.txnBox}>
                <div style={S.txnRow}><span>Amount</span><span>₹{txn?.amount?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span></div>
                <div style={S.txnRow}><span>Transaction ID</span><span style={{ fontFamily: 'monospace', fontSize: 12 }}>{txn?.a1x_transaction_id}</span></div>
              </div>
            )}
            <button onClick={handleNewPayment} style={S.btn}>Try Again</button>
          </div>
        )}
      </div>
    </div>
  );
}

const S = {
  page:  { minHeight: '100vh', background: '#f9f9fc', fontFamily: '"Plus Jakarta Sans", system-ui, Arial, sans-serif' },
  nav:   { background: '#14142b', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 24px' },
  navBrand: { display: 'flex', alignItems: 'center', fontWeight: 700, fontSize: 18 },
  navRight: { display: 'flex', alignItems: 'center', gap: 8 },
  navBtn:   { background: 'none', border: '1px solid #334155', color: '#6e6e86', borderRadius: 8, padding: '6px 10px', cursor: 'pointer' },
  body:  { display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: '48px 16px' },
  card:  { width: '100%', maxWidth: 480, background: '#fff', borderRadius: 16, padding: '36px 32px', boxShadow: '0 4px 24px rgba(0,0,0,.1)' },
  title: { fontSize: 24, fontWeight: 700, color: '#14142b', margin: '0 0 8px' },
  sub:   { fontSize: 14, color: '#4a4a6a', marginBottom: 28 },
  errBox: { background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 10, padding: '12px 14px', color: '#DC2626', fontSize: 13, marginBottom: 20 },
  amtRow: { position: 'relative', marginBottom: 20 },
  rupee:  { position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', color: '#14142b', fontSize: 22, pointerEvents: 'none' },
  amtInput: { width: '100%', padding: '18px 16px 18px 48px', fontSize: 32, fontWeight: 700, border: '2px solid #e8e8f1', borderRadius: 14, outline: 'none', color: '#14142b', boxSizing: 'border-box', background: '#f9f9fc' },
  fieldLabel: { display: 'block', fontSize: 12, fontWeight: 600, color: '#4a4a6a', marginBottom: 6 },
  noteInput: { width: '100%', padding: '11px 14px', border: '1.5px solid #e8e8f1', borderRadius: 10, fontSize: 14, outline: 'none', color: '#14142b', background: '#f9f9fc', boxSizing: 'border-box' },
  btn: { width: '100%', background: '#14142b', color: '#fff', border: 0, borderRadius: 10, padding: '14px', fontSize: 15, fontWeight: 700, cursor: 'pointer', display: 'block', marginBottom: 0 },
  bigIcon: { fontSize: 64, marginBottom: 16 },
  statusTitle: { fontSize: 22, fontWeight: 700, color: '#14142b', margin: '0 0 8px' },
  statusSub:   { fontSize: 14, color: '#4a4a6a', marginBottom: 24 },
  txnBox: { background: '#f9f9fc', borderRadius: 10, padding: '16px', marginBottom: 24, textAlign: 'left' },
  txnRow: { display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '6px 0', borderBottom: '1px solid #e8e8f1', color: '#4a4a6a' },
};
